import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import Layout from './components/Layout';
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import LoadingSpinner from './components/LoadingSpinner';

const SearchDomainRoute = lazy(() => import('./components/SearchDomainRoute'));
const LogoSearchPage = lazy(() => import('./pages/LogoSearchPage'));
const LogoSubmitPage = lazy(() => import('./pages/LogoSubmitPage'));
const SearchPage = lazy(() => import('./pages/SearchPage'));
const SubmitPage = lazy(() => import('./pages/SubmitPage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const EditDemoPage = lazy(() => import('./pages/EditDemoPage'));
const LogoAdminPage = lazy(() => import('./pages/LogoAdminPage'));

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

// Admin-only route - requires demo-editors Azure AD group
function AdminRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, isDemoEditor } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!isDemoEditor) {
    return <Navigate to="/demos/search" replace />;
  }

  return <>{children}</>;
}

function App() {
  return (
    <Suspense
      fallback={
        <div className="min-h-screen flex items-center justify-center">
          <LoadingSpinner size="lg" />
        </div>
      }
    >
      <Routes>
      {/* Auth routes */}
      <Route path="/login" element={<LoginPage />} />

      {/* Protected routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<LandingPage />} />
        <Route
          path="logos/search"
          element={
            <SearchDomainRoute domain="logo">
              <LogoSearchPage />
            </SearchDomainRoute>
          }
        />
        <Route path="logos/submit" element={<LogoSubmitPage />} />
        <Route path="search" element={<Navigate to="/demos/search" replace />} />
        <Route path="submit" element={<Navigate to="/demos/submit" replace />} />
        <Route
          path="demos/search"
          element={
            <SearchDomainRoute domain="demo">
              <SearchPage />
            </SearchDomainRoute>
          }
        />
        <Route path="demos/submit" element={<SubmitPage />} />
        
        {/* Admin routes - requires demo-editors group */}
        <Route
          path="admin"
          element={
            <AdminRoute>
              <AdminPage />
            </AdminRoute>
          }
        />
        <Route
          path="admin/logos"
          element={
            <AdminRoute>
              <LogoAdminPage />
            </AdminRoute>
          }
        />
        <Route
          path="admin/demos/:demoId/edit"
          element={
            <AdminRoute>
              <EditDemoPage />
            </AdminRoute>
          }
        />
        <Route
          path="admin/logos/:logoId/edit"
          element={
            <AdminRoute>
              <Navigate to="/admin/logos" replace />
            </AdminRoute>
          }
        />
      </Route>

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

export default App;
